// import { AuthClient } from '@tempo-io/auth-client';
// import { Fim3ApiClient } from '@tempo-io/fim3-api-client';

import { useAuth0 } from '@auth0/auth0-react';
import axios, { type AxiosInstance } from 'axios';
import type { TempoUserResponse } from '@/lib/api/types/userManagement';
import { getEnv } from '@/lib/utils/common/getEnv';
import { useAccountSettingsApi } from './useAccountSettingsApi';
import { useActivityLogsApi } from './useActivityLogsApi';
import { useBulkImportApi } from './useBulkImportApi';
import { useCompletionEventsApi } from './useCompletionEventsApi';
import { useCompletionNotificationsApi } from './useCompletionNotificationsApi';
import { useCustomersApi } from './useCustomersApi';
import { useCustomFieldsApi } from './useCustomFieldsApi';
import { useDemandsApi } from './useDemandsApi';
import { useDemandViewsApi } from './useDemandViewsApi';
import { useFeedbackApi } from './useFeedbackApi';
import { useFeedbackFormsApi } from './useFeedbackFormsApi';
import { useIdeasApi } from './useIdeasApi';
import { useIdeaViewsApi } from './useIdeaViewsApi';
import { useVotesApi } from './useIdeaVotesApi';
import { useNotificationSubscriptions } from './useNotificationSubscriptions';
import { usePortalsApi } from './usePortalsApi';
import { usePriorityFrameworkApi } from './usePriorityFrameworkApi';
import { useProductAreasApi } from './useProductAreasApi';
import { useRanksApi } from './useRanksApi';
import { useS3Api } from './useS3Api';
import { useUserInteractionsApi } from './useUserInteractionsApi';

interface IData {
  id: number;
  displayName?: string | null | undefined;
  creatorName?: string | null | undefined;
  email?: string | null | undefined;
  emailAddress?: string | null | undefined;
}

export const getUserDataForResults = async (
  httpClient: AxiosInstance,
  userIds: string[],
  results: IData[],
  getUserIdInObject: (comment: IData) => string,
) => {
  const usersUrl = `${getEnv().apiBaseUrl}/api/users/v1`;

  const distinctUserIds = userIds
    .filter((el) => !!el)
    .filter((userId, index) => userIds.indexOf(userId) === index);

  if (distinctUserIds.length === 0) {
    return results;
  }

  const params = new URLSearchParams();
  distinctUserIds.forEach((id) => {
    params.append('id', id);
  });

  const { data } = await httpClient.get(`${usersUrl}`, {
    params: {
      limit: '50',
      filter: JSON.stringify({
        field: 'id',
        operator: 'in',
        value: distinctUserIds,
      }),
    },
  });

  const accountUsers: TempoUserResponse[] = data.items;

  const resultsWithUserData = results.map((item) => {
    let name: string | undefined = item.displayName ?? item.creatorName ?? undefined;
    let email: string | undefined = item.emailAddress ?? item.email ?? undefined;
    const userId = getUserIdInObject(item);
    if (userId && userId.length > 0) {
      name = 'Unknown';
      const userFound = accountUsers.find((user) => user.id === userId);
      if (userFound) {
        name = userFound.name;
        email = userFound.email;
      }
    }
    item = { ...item, displayName: name, creatorName: name, emailAddress: email };
    return item;
  });

  return resultsWithUserData;
};

export const useFim3Api = () => {
  const { getAccessTokenSilently } = useAuth0();

  // Create authenticated axios client for FIM API
  const httpClient = axios.create({
    baseURL: `${getEnv().fimApiLocalBaseUrl ?? getEnv().apiBaseUrl}/api/v1/fim`,
  });

  // Add auth interceptor to include SPM token
  httpClient.interceptors.request.use(async (config) => {
    try {
      const token = await getAccessTokenSilently();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (_error) {
      // Failed to get access token - request will proceed without authentication
    }
    return config;
  });

  // TODO: Use API client instead of low-level http/s3 client(s)
  const productAreasApi = useProductAreasApi(httpClient);
  const feedbackFormsApi = useFeedbackFormsApi(httpClient);
  const ideasApi = useIdeasApi(httpClient);
  const ideaViewsApi = useIdeaViewsApi(httpClient);
  const demandsApi = useDemandsApi(httpClient);
  const demandViewsApi = useDemandViewsApi(httpClient);
  const customFieldsApi = useCustomFieldsApi(httpClient);
  const feedbackApi = useFeedbackApi(httpClient);
  const votesApi = useVotesApi(httpClient);
  const ranksApi = useRanksApi(httpClient);
  const s3Api = useS3Api(httpClient);
  const activityLogsApi = useActivityLogsApi(httpClient);
  const customersApi = useCustomersApi(httpClient);
  const accountSettingsApi = useAccountSettingsApi(httpClient);
  const bulkImportApi = useBulkImportApi(httpClient);
  const portalsApi = usePortalsApi(httpClient);
  const priorityFrameworkApi = usePriorityFrameworkApi(httpClient);
  const notificationSubscriptionsApi = useNotificationSubscriptions(httpClient);
  const completionEventsApi = useCompletionEventsApi(httpClient);
  const completionNotificationsApi = useCompletionNotificationsApi(httpClient);
  const userInteractionsApi = useUserInteractionsApi(httpClient);

  return {
    productAreasApi,
    feedbackFormsApi,
    ideasApi,
    ideaViewsApi,
    demandsApi,
    demandViewsApi,
    votesApi,
    ranksApi,
    customFieldsApi,
    feedbackApi,
    s3Api,
    activityLogsApi,
    customersApi,
    accountSettingsApi,
    bulkImportApi,
    portalsApi,
    httpClient,
    priorityFrameworkApi,
    notificationSubscriptionsApi,
    completionEventsApi,
    completionNotificationsApi,
    userInteractionsApi,
  };
};
